import { MobileFilterGroup } from 'components/Filters/components/FilterModal'
import { DateRangeFilter } from 'components/Filters/fields/DateRangeFilter'
import {
  ContactCountRangeFilter,
  NoteCountRangeFilter,
  RangeFilter,
} from 'components/Filters/fields/RangeFilter'
import {
  SelectFilter,
  SelectFilterOption,
} from 'components/Filters/fields/SelectFilter'
import { useCategorySubcategoryOptions } from 'hooks/useCategorySubcategoryFilterOptions'
import useApiService from 'services/api'
import { useListProductsCatalog } from 'services/products'

import { FilterIdentifier } from 'models/saved_view'
import { filterStoreRepo } from 'components/Filters/FilterStore'
import { createFilterColumnHelper } from 'components/DataExpansionComponents/Common/commonColumnHelpers'
import { DEAL_COLUMNS } from 'components/DataExpansionComponents/Deals/dealsColumnInfo'
import { useFeatureFlagService } from 'utils/useFeatureFlagService'
import { useMemo } from 'react'
import SelectSavedFilter from 'components/Filters/saved-filters/SelectSavedFilter'
import { FilterChips } from 'components/Filters/components/FilterChips/FilterChips'
import { MobileFilterCriteriaSelect } from 'components/MobileFilterCriteriaSelect'

export function MobileDealsFilterset(props: {
  filterIdentifier: FilterIdentifier
  hideCampaignsFilter?: boolean
}) {
  const api = useApiService()
  const { data: userOptions } = api.useGetUserOptions({
    onlyActive: true,
    withNoneOption: true,
  })
  const { data: stageOptions } = api.useGetSalesStageOptions(true)
  const { data: tagsOptions } = api.useGetTagsOptions(true)

  // Use new products catalog API instead of legacy API
  const { data: catalogResponse } = useListProductsCatalog({ page_size: 1000 })

  const { data: productVariantsOptions } =
    api.useGetProductVariantsOptions(true)
  const { data: distributorOptions } =
    api.useGetCompanyDistributorsOptions(true)
  const { data: gpoOptions } = api.useGetCompanyGPOsOptions(true)
  const { data: contactTagsOptions } = api.useGetTagsOptions(true, 'contacts')
  const dealsFilterStore = filterStoreRepo.getStore(props.filterIdentifier)
  const { categoriesOptions, subCategoriesOptions } =
    useCategorySubcategoryOptions({ filterStore: dealsFilterStore })
  const { data: campaignOptions } = api.useGetCampaignsOptions()
  const { enableEmailIntegration, enabledReportDeals } = useFeatureFlagService()
  const filterStore = filterStoreRepo.getStore(props.filterIdentifier)

  const reportsDisabledTooltip = !enabledReportDeals
    ? 'Revenue calculations are currently down for maintenance.'
    : undefined

  // Convert product catalog response to SelectFilter options
  const productsOptions: SelectFilterOption[] = useMemo(() => {
    if (!catalogResponse?.results) return []
    return catalogResponse.results.map((product) => ({
      label: product.name ?? '',
      value: String(product.id),
    }))
  }, [catalogResponse])

  // This filterset uses DEAL_COLUMNS which works for both V1 (/api/deals/) and V2 (/api/reports/deals/).
  // Both use the unified DEALS_TABLE identifier with backend field aliases for compatibility.
  const dealFilterHelper = createFilterColumnHelper({ columns: DEAL_COLUMNS })

  return (
    <>
      <div className="mb-2">
        <SelectSavedFilter
          filterStore={filterStore}
          identifier={props.filterIdentifier}
        />
      </div>
      <MobileFilterGroup title={'Target Details'}>
        <RangeFilter
          {...dealFilterHelper.retrieveProps('company_size')}
          store={dealsFilterStore}
        />
        <SelectFilter
          {...dealFilterHelper.retrieveProps('category')}
          options={categoriesOptions}
          filterStore={dealsFilterStore}
        />
        <SelectFilter
          {...dealFilterHelper.retrieveProps('sub_category')}
          options={subCategoriesOptions}
          filterStore={dealsFilterStore}
        />
        <RangeFilter
          {...dealFilterHelper.retrieveProps('estimated_meals_per_day')}
          store={dealsFilterStore}
        />
        <RangeFilter
          {...dealFilterHelper.retrieveProps('operational_schools')}
          store={dealsFilterStore}
        />
        <RangeFilter
          {...dealFilterHelper.retrieveProps('doors_count')}
          store={dealsFilterStore}
        />
      </MobileFilterGroup>
      <MobileFilterGroup title="Account">
        <SelectFilter
          {...dealFilterHelper.retrieveProps('product')}
          options={productsOptions}
          filterStore={dealsFilterStore}
        />
        <SelectFilter
          {...dealFilterHelper.retrieveProps('product_variant')}
          options={productVariantsOptions as any}
          filterStore={dealsFilterStore}
        />
        <SelectFilter
          {...dealFilterHelper.retrieveProps('sales_stage')}
          options={stageOptions as any}
          filterStore={dealsFilterStore}
        />
        <SelectFilter
          {...dealFilterHelper.retrieveProps('account_owner')}
          options={userOptions as any}
          filterStore={dealsFilterStore}
        />
        <SelectFilter
          {...dealFilterHelper.retrieveProps('taglist')}
          options={tagsOptions as any}
          filterStore={dealsFilterStore}
        />
        <SelectFilter
          {...dealFilterHelper.retrieveProps('contact_tags')}
          options={contactTagsOptions as any}
          filterStore={dealsFilterStore}
        />
        <SelectFilter
          {...dealFilterHelper.retrieveProps('distributors')}
          options={distributorOptions as any}
          filterStore={dealsFilterStore}
        />
        <SelectFilter
          {...dealFilterHelper.retrieveProps('gpos')}
          options={gpoOptions as any}
          filterStore={dealsFilterStore}
        />
        <NoteCountRangeFilter store={dealsFilterStore} />
        <ContactCountRangeFilter store={dealsFilterStore} />
        {!props.hideCampaignsFilter && (
          <SelectFilter
            {...dealFilterHelper.retrieveProps('origin_campaign')}
            filterKey="origin_campaign"
            options={campaignOptions as any}
            filterStore={dealsFilterStore}
          />
        )}
      </MobileFilterGroup>
      <MobileFilterGroup title="Predicted Values">
        <RangeFilter
          {...dealFilterHelper.retrieveProps('monthly_volume_predicted')}
          decimalScale={1}
          store={dealsFilterStore}
          disabled={!enabledReportDeals}
          disabledTooltipText={reportsDisabledTooltip}
        />
        <RangeFilter
          {...dealFilterHelper.retrieveProps('monthly_revenue_predicted')}
          store={dealsFilterStore}
          disabled={!enabledReportDeals}
          disabledTooltipText={reportsDisabledTooltip}
        />
        <DateRangeFilter
          {...dealFilterHelper.retrieveProps('expected_days_to_close')}
          filterStore={dealsFilterStore}
        />
      </MobileFilterGroup>
      <MobileFilterGroup title="Overrides">
        <RangeFilter
          {...dealFilterHelper.retrieveProps('monthly_volume_override')}
          decimalScale={1}
          store={dealsFilterStore}
        />
        <RangeFilter
          {...dealFilterHelper.retrieveProps('monthly_revenue_override')}
          store={dealsFilterStore}
        />
        <DateRangeFilter
          {...dealFilterHelper.retrieveProps('close_date_override')}
          filterStore={dealsFilterStore}
        />
      </MobileFilterGroup>
      <MobileFilterGroup title="User Activity">
        <DateRangeFilter
          {...dealFilterHelper.retrieveProps('modified')}
          filterStore={dealsFilterStore}
        />
        <DateRangeFilter
          {...dealFilterHelper.retrieveProps('created')}
          filterStore={dealsFilterStore}
        />
        <RangeFilter
          {...dealFilterHelper.retrieveProps('last_datetime_stage_changed')}
          decimalScale={1}
          store={dealsFilterStore}
          minAllowNegative={false}
          maxAllowNegative={false}
        />
        {enableEmailIntegration && (
          <>
            <RangeFilter
              {...dealFilterHelper.retrieveProps('days_since_reply')}
              decimalScale={0}
              store={dealsFilterStore}
              minAllowNegative={false}
              maxAllowNegative={false}
            />
            <RangeFilter
              {...dealFilterHelper.retrieveProps('days_since_sent')}
              decimalScale={0}
              store={dealsFilterStore}
              minAllowNegative={false}
              maxAllowNegative={false}
            />
          </>
        )}
        <RangeFilter
          {...dealFilterHelper.retrieveProps('days_since_last_modified')}
          store={dealsFilterStore}
        />
        <RangeFilter
          {...dealFilterHelper.retrieveProps('days_since_deal_last_modified')}
          store={dealsFilterStore}
        />
      </MobileFilterGroup>

      <MobileFilterCriteriaSelect filterStore={dealsFilterStore} />
      <FilterChips
        identifier={props.filterIdentifier}
        store={dealsFilterStore}
        className="ml-0"
        clearAllButton
      />
    </>
  )
}
